import { useState } from 'react';
import { useRoute, useLocation } from 'wouter';
import { Plus, ArrowLeft } from 'lucide-react';
import { AppShell } from '@/components/app-shell';
import { RfqList } from '@/components/rfq-list';
import { RfqDetail } from '@/components/rfq-detail';
import { ParseEmailModal } from '@/components/parse-email-modal';

// RFQ console — list on the left, detail on the right when an RFQ is selected.
// On mobile (<lg), shows ONE panel at a time: list when no detail selected,
// detail with a back button when one is.

export function Home() {
  const [match, params] = useRoute('/rfqs/:publicId');
  const selectedRfqId = match ? params.publicId : null;
  const [parseOpen, setParseOpen] = useState(false);
  const [, setLocation] = useLocation();

  return (
    <AppShell
      title="RFQs"
      subtitle="Inbound requests for quote — parsed, stage-tracked, dispatched to GHL"
      headerActions={
        <>
          {selectedRfqId && (
            <button
              onClick={() => setLocation('/rfqs')}
              className="lg:hidden btn-ghost px-2"
              aria-label="Back to RFQ list"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          )}
          <button onClick={() => setParseOpen(true)} className="btn-primary">
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">New from email</span>
            <span className="sm:hidden">New</span>
          </button>
        </>
      }
    >
      {/* Desktop: two-column split. Mobile: show list OR detail, never both.
       *  `h-full` on aside + section is required on mobile — without it the
       *  section's height collapses to content, parent's overflow-hidden
       *  clips it, and the inner overflow-auto never fires. */}
      <div className="h-full lg:grid lg:grid-cols-[minmax(360px,40%)_1fr] overflow-hidden">
        <aside
          className={`h-full border-r border-border overflow-hidden bg-surface ${
            selectedRfqId ? 'hidden lg:block' : 'block'
          }`}
        >
          <RfqList selectedRfqId={selectedRfqId} />
        </aside>
        <section
          className={`h-full overflow-hidden bg-surface ${
            selectedRfqId ? 'block' : 'hidden lg:block'
          }`}
        >
          {selectedRfqId ? (
            <RfqDetail key={selectedRfqId} publicId={selectedRfqId} />
          ) : (
            <div className="h-full flex items-center justify-center px-8">
              <div className="text-center max-w-md">
                <div className="w-12 h-12 mx-auto rounded-full bg-primary-muted text-primary-strong flex items-center justify-center mb-3">
                  <Plus className="w-6 h-6" />
                </div>
                <p className="text-sm text-foreground-muted">
                  Select an RFQ on the left, or click <span className="font-medium text-foreground">New from email</span> to parse one.
                </p>
              </div>
            </div>
          )}
        </section>
      </div>

      <ParseEmailModal open={parseOpen} onClose={() => setParseOpen(false)} />
    </AppShell>
  );
}
